import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { LogIn, Eye, EyeOff, RefreshCw, ArrowLeft, Search, Copy, ShieldCheck, ShieldAlert, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTO_HIDE_MS = 30000;

/**
 * Admin → Group Logins
 * Grid of every group's HEAD + ASSISTANT credentials. The head password is
 * always hidden until the admin taps "Reveal" — once revealed, it auto-hides
 * after 30s so it can't sit on screen during a screen-share.
 */
export default function AdminGroupLoginsView({ token, onBack }) {
  const [rows, setRows] = useState([]);
  const [supervisorRows, setSupervisorRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [revealed, setRevealed] = useState({}); // group_code → expires_at ms epoch
  const [confirmReset, setConfirmReset] = useState(null); // group row
  const [overrideTarget, setOverrideTarget] = useState(null); // group row for admin force-set
  const [overridePw, setOverridePw] = useState("");
  const [busy, setBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const mirror = async (bodyPayload) => {
    try {
      const res = await axios.post(`${API}/auth/impersonate`, bodyPayload, { headers });
      localStorage.setItem("admin_return_token", token);
      localStorage.setItem("token", res.data.token);
      window.location.href = res.data.route;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mirror login failed");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groupsRes, supRes] = await Promise.all([
        axios.get(`${API}/admin/group-logins`, { headers }),
        axios.get(`${API}/admin/supervisor-logins`, { headers }),
      ]);
      setRows(groupsRes.data?.groups || []);
      setSupervisorRows(supRes.data?.divisions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load group logins");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Auto-collapse revealed entries after 30 seconds
  useEffect(() => {
    if (Object.keys(revealed).length === 0) return;
    const tick = setInterval(() => {
      const now = Date.now();
      setRevealed(prev => {
        const next = {};
        for (const [k, exp] of Object.entries(prev)) {
          if (exp > now) next[k] = exp;
        }
        return Object.keys(next).length === Object.keys(prev).length ? prev : next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [revealed]);

  const reveal = (code) => {
    setRevealed(prev => ({ ...prev, [code]: Date.now() + AUTO_HIDE_MS }));
  };
  const hide = (code) => {
    setRevealed(prev => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — long-press to select manually");
    }
  };

  const resetGroup = async (row) => {
    setBusy(true);
    try {
      const url = row.is_supervisor
        ? `${API}/admin/supervisor-logins/${row.division}/reset`
        : `${API}/admin/group-logins/${row.group_code}/reset`;
      await axios.post(url, {}, { headers });
      toast.success(`Cleared password for ${row.is_supervisor ? row.division_name + " supervisor" : row.group_code}. They'll be forced to set a new one on next login.`);
      setConfirmReset(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const forceSet = async () => {
    if (!overrideTarget) return;
    const pw = (overridePw || "").trim();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (/\s/.test(pw)) { toast.error("Password cannot contain spaces"); return; }
    setBusy(true);
    try {
      const url = overrideTarget.is_supervisor
        ? `${API}/admin/supervisor-logins/${overrideTarget.division}/set`
        : `${API}/admin/group-logins/${overrideTarget.group_code}/set`;
      await axios.post(url, { new_password: pw }, { headers });
      toast.success(`Password set for ${overrideTarget.is_supervisor ? overrideTarget.division_name + " supervisor" : "head" + overrideTarget.group_code}.`);
      setOverrideTarget(null);
      setOverridePw("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set password");
    } finally {
      setBusy(false);
    }
  };

  const filtered = rows.filter(r => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return r.group_code.toLowerCase().includes(q) || (r.group_name || "").toLowerCase().includes(q);
  });

  const setCount = rows.filter(r => r.password_set).length;
  const pendingCount = rows.filter(r => !r.password_set).length;

  return (
    <div className="space-y-4" data-testid="admin-group-logins-view">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="px-2" data-testid="group-logins-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-rose-600" /> Group Logins
        </h2>
        <Button variant="outline" size="sm" onClick={load} className="ml-auto" data-testid="group-logins-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-3 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            <div>
              <p className="text-xl font-extrabold text-emerald-800 leading-tight">{setCount}</p>
              <p className="text-[10px] text-emerald-700 font-medium">custom password set</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
            <div>
              <p className="text-xl font-extrabold text-amber-800 leading-tight">{pendingCount}</p>
              <p className="text-[10px] text-amber-700 font-medium">still using shared</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-3 flex items-center gap-2">
            <LogIn className="w-6 h-6 text-slate-600" />
            <div>
              <p className="text-xl font-extrabold text-slate-800 leading-tight">{rows.length}</p>
              <p className="text-[10px] text-slate-700 font-medium">total groups</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Supervisor (division) logins ── */}
      {supervisorRows.length > 0 && (
        <div className="rounded-2xl border-2 border-violet-200 bg-violet-50/40 p-3 space-y-2" data-testid="supervisor-logins-section">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-violet-700" />
            <h3 className="text-sm font-bold text-violet-900">Supervisor Logins (Divisional)</h3>
            <span className="ml-auto text-[10px] text-violet-700">
              {supervisorRows.filter(s => s.password_set).length} of {supervisorRows.length} have set custom passwords
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {supervisorRows.map(sup => {
              const key = `sup-${sup.division}`;
              const isRevealed = !!revealed[key];
              const expiresIn = isRevealed ? Math.max(0, Math.ceil((revealed[key] - Date.now()) / 1000)) : 0;
              const rowAsTarget = { ...sup, is_supervisor: true, group_code: sup.division, head_password: sup.password };
              return (
                <Card
                  key={sup.division}
                  className={`${sup.password_set ? "border-emerald-300 bg-white" : "border-amber-300 bg-amber-50/40"}`}
                  data-testid={`supervisor-login-card-${sup.division}`}
                >
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-bold text-slate-800">{sup.division_name}</p>
                        <p className="text-[11px] text-muted-foreground">supervisor login</p>
                      </div>
                      {sup.password_set ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Custom</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Shared</Badge>
                      )}
                    </div>
                    <div className="rounded-lg bg-white border border-slate-200 p-2 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase font-bold text-slate-500 w-14">User</span>
                        <code className="text-xs font-mono text-slate-900 flex-1 truncate" data-testid={`sup-user-${sup.division}`}>{sup.username}</code>
                        <button type="button" onClick={() => copyToClipboard(sup.username)} className="text-slate-400 hover:text-slate-700" aria-label="Copy">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase font-bold text-slate-500 w-14">Pwd</span>
                        <code className="text-xs font-mono text-slate-900 flex-1 truncate" data-testid={`sup-pwd-${sup.division}`}>
                          {isRevealed ? sup.password : "•".repeat(Math.min(12, Math.max(8, (sup.password || "").length)))}
                        </code>
                        {isRevealed ? (
                          <>
                            <span className="text-[9px] text-amber-700 font-semibold">{expiresIn}s</span>
                            <button type="button" onClick={() => copyToClipboard(sup.password)} className="text-slate-400 hover:text-slate-700" aria-label="Copy"><Copy className="w-3.5 h-3.5" /></button>
                            <button type="button" onClick={() => hide(key)} className="text-slate-500 hover:text-slate-800" data-testid={`sup-hide-${sup.division}`} aria-label="Hide"><EyeOff className="w-3.5 h-3.5" /></button>
                          </>
                        ) : (
                          <button type="button" onClick={() => reveal(key)} className="text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 text-[10px] font-semibold" data-testid={`sup-reveal-${sup.division}`}>
                            <Eye className="w-3.5 h-3.5" /> Reveal
                          </button>
                        )}
                      </div>
                      {sup.password_set && sup.set_at && (
                        <p className="text-[9px] text-emerald-700">
                          Set by {sup.set_by || "supervisor"} · {new Date(sup.set_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button variant="outline" size="sm" onClick={() => mirror({ target: "supervisor", division: sup.division })} className="text-[10px] h-7 px-2 flex-1 text-violet-700 border-violet-300 hover:bg-violet-50" data-testid={`sup-mirror-${sup.division}`}>
                        <LogIn className="w-3 h-3 mr-1" /> Mirror
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setOverrideTarget(rowAsTarget); setOverridePw(""); }} className="text-[10px] h-7 px-2 flex-1" data-testid={`sup-override-${sup.division}`}>
                        <KeyRound className="w-3 h-3 mr-1" /> Set new
                      </Button>
                      {sup.password_set && (
                        <Button variant="outline" size="sm" onClick={() => setConfirmReset(rowAsTarget)} className="text-[10px] h-7 px-2 flex-1 text-rose-700 hover:bg-rose-50" data-testid={`sup-reset-${sup.division}`}>
                          <RefreshCw className="w-3 h-3 mr-1" /> Force reset
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by group code or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="group-logins-search"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No groups match "{search}".</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="group-logins-grid">
          {filtered.map(row => {
            const isRevealed = !!revealed[row.group_code];
            const expiresIn = isRevealed ? Math.max(0, Math.ceil((revealed[row.group_code] - Date.now()) / 1000)) : 0;
            return (
              <Card
                key={row.group_code}
                className={`hover:shadow-md transition-shadow ${row.password_set ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`}
                data-testid={`group-login-card-${row.group_code}`}
              >
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-bold text-slate-800">{row.group_code}</p>
                      <p className="text-[11px] text-muted-foreground">{row.group_name}</p>
                    </div>
                    {row.password_set ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Custom</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Shared</Badge>
                    )}
                  </div>

                  <div className="rounded-lg bg-white border border-slate-200 p-2 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase font-bold text-slate-500 w-16">Head user</span>
                      <code className="text-xs font-mono text-slate-900 flex-1 truncate" data-testid={`gl-head-user-${row.group_code}`}>{row.head_username}</code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(row.head_username)}
                        className="text-slate-400 hover:text-slate-700"
                        aria-label="Copy username"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase font-bold text-slate-500 w-16">Head pwd</span>
                      <code className="text-xs font-mono text-slate-900 flex-1 truncate" data-testid={`gl-head-pwd-${row.group_code}`}>
                        {isRevealed ? row.head_password : "•".repeat(Math.min(10, (row.head_password || "").length || 8))}
                      </code>
                      {isRevealed ? (
                        <>
                          <span className="text-[9px] text-amber-700 font-semibold">{expiresIn}s</span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(row.head_password)}
                            className="text-slate-400 hover:text-slate-700"
                            aria-label="Copy password"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => hide(row.group_code)}
                            className="text-slate-500 hover:text-slate-800"
                            data-testid={`gl-hide-${row.group_code}`}
                            aria-label="Hide password"
                          >
                            <EyeOff className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => reveal(row.group_code)}
                          className="text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 text-[10px] font-semibold"
                          data-testid={`gl-reveal-${row.group_code}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> Reveal
                        </button>
                      )}
                    </div>
                    {row.password_set && row.set_at && (
                      <p className="text-[9px] text-emerald-700">
                        Set by {row.set_by || "head"} · {new Date(row.set_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </p>
                    )}
                  </div>

                  {row.assistant_username && (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase font-bold text-slate-500 w-16">Asst user</span>
                        <code className="text-xs font-mono text-slate-700 flex-1 truncate">{row.assistant_username}</code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase font-bold text-slate-500 w-16">Asst pwd</span>
                        <code className="text-xs font-mono text-slate-700 flex-1 truncate">{row.assistant_password}</code>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => mirror({ target: "head", group_code: row.group_code })}
                      className="text-[10px] h-7 px-2 flex-1 text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                      data-testid={`gl-mirror-head-${row.group_code}`}
                    >
                      <LogIn className="w-3 h-3 mr-1" /> Mirror Head
                    </Button>
                    {row.assistant_username && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => mirror({ target: "assistant", group_code: row.group_code })}
                        className="text-[10px] h-7 px-2 flex-1 text-slate-700 hover:bg-slate-50"
                        data-testid={`gl-mirror-asst-${row.group_code}`}
                      >
                        <LogIn className="w-3 h-3 mr-1" /> Mirror Asst
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setOverrideTarget(row); setOverridePw(""); }}
                      className="text-[10px] h-7 px-2 flex-1"
                      data-testid={`gl-override-${row.group_code}`}
                    >
                      <KeyRound className="w-3 h-3 mr-1" /> Set new
                    </Button>
                    {row.password_set && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmReset(row)}
                        className="text-[10px] h-7 px-2 flex-1 text-rose-700 hover:bg-rose-50"
                        data-testid={`gl-reset-${row.group_code}`}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" /> Force reset
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirm force-reset */}
      <Dialog open={!!confirmReset} onOpenChange={(v) => !v && setConfirmReset(null)}>
        <DialogContent className="max-w-md" data-testid="gl-confirm-reset-modal">
          <DialogHeader>
            <DialogTitle className="text-rose-700">
              Force reset {confirmReset?.is_supervisor ? `${confirmReset?.division_name} supervisor` : confirmReset?.group_code} password?
            </DialogTitle>
            <DialogDescription>
              {confirmReset?.is_supervisor ? (
                <>The <strong>{confirmReset?.division_name}</strong> supervisor will be forced to set a new password on their next login. The current password will stop working immediately.</>
              ) : (
                <>The head counselor for <strong>{confirmReset?.group_code}</strong> ({confirmReset?.group_name}) will be forced to set a new password on their next login. The current password ({confirmReset?.head_password}) will stop working immediately.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmReset(null)}>Cancel</Button>
            <Button
              size="sm"
              onClick={() => resetGroup(confirmReset)}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="gl-confirm-reset-yes"
            >
              {busy ? "Resetting…" : "Yes, force reset"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin force-set override */}
      <Dialog open={!!overrideTarget} onOpenChange={(v) => !v && setOverrideTarget(null)}>
        <DialogContent className="max-w-md" data-testid="gl-override-modal">
          <DialogHeader>
            <DialogTitle>
              Set password for {overrideTarget?.is_supervisor ? `${overrideTarget?.division_name} supervisor` : `head${overrideTarget?.group_code}`}
            </DialogTitle>
            <DialogDescription>
              Admin override — directly set this {overrideTarget?.is_supervisor ? "supervisor's" : "group's head"} password.
              Use this if they call in and can't reach the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              type="text"
              value={overridePw}
              onChange={(e) => setOverridePw(e.target.value)}
              placeholder="New password (min 6 chars, no spaces)"
              autoFocus
              data-testid="gl-override-input"
            />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setOverrideTarget(null)}>Cancel</Button>
            <Button
              size="sm"
              onClick={forceSet}
              disabled={busy || !overridePw}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="gl-override-save"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
